import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, Group, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { i18n } from '@lingui/core';
import {
  createCustomAttribute,
  createCustomAttributeGroup,
  createCustomAttributeInDefaultGroup,
  fetchCustomAttributeGroups,
} from '../../../Service/restapi/customAttributeService';
import { hasSpecialChar, replaceUmlaute } from '../../../Utils/utils';
import { ToastS } from '../../../Service/ToastS';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { DataS } from '../../../Service/DataS';

const MODAL_ID = 'custom-attribute-create-modal';

export const openCustomAttributeCreateModal = ({ entityType, finish }) =>
  openModal({
    modalId: MODAL_ID,
    size: 'md',
    title: (
      <Text size="xl" weight="bolder">
        {i18n._('custom-attribute.create')}
      </Text>
    ),
    children: <CustomAttributeCreateModal entityType={entityType} finish={finish} />,
  });

const CustomAttributeCreateModal = ({ entityType, finish }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [attributeType, setAttributeType] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCustomAttributeGroups(entityType)
      .then(async (resp) => {
        if (!resp.ok) {
          console.error(`could not get groups of entityType ${entityType}`);
          return;
        }

        const fetchedGroups = await resp.json();
        setGroups(fetchedGroups);

        const defaultGroup = fetchedGroups.find((group) => group.name === 'Standard');
        if (defaultGroup && entityType !== 'contact') {
          setGroupName(defaultGroup.name);
        }
      })
      .catch(() => console.error(`could not get groups of entityType ${entityType}`));
  }, [entityType]);

  const createAttribute = async (groupId) => {
    const isSelect = attributeType === 'SELECT';
    const finalAttributeType = isSelect ? 'STRING' : attributeType;
    const resp = groupId
      ? await createCustomAttribute(name, description, finalAttributeType, groupId, isSelect)
      : await createCustomAttributeInDefaultGroup(name, description, finalAttributeType, entityType, isSelect);

    if (!resp.ok) {
      console.error(`could not create new custom attribute ${name} of attributeType ${finalAttributeType}`);
      return;
    }

    ToastS.success('field-created', 'Eigenes Feld erstellt');
    MixpanelS.track(ConstantS.TrackingEvents.CustomAttibuteCreated);
    finish();
    closeModal(MODAL_ID);
  };

  const create = async () => {
    if (!name || !attributeType) {
      ToastS.info('create-result', 'Bitte vergebe dem Feld einen Namen und wähle einen Feld-Typ aus.');
      return;
    }

    if (hasSpecialChar(name) || hasSpecialChar(groupName)) {
      ToastS.info('create-result', 'Sonderzeichen sind im Namen nicht erlaubt.');
      return;
    }

    setLoading(true);
    try {
      const existingGroup = groups.find((group) => group.name === groupName);

      if (existingGroup) {
        await createAttribute(existingGroup.id);
      } else if (groupName) {
        const groupResp = await createCustomAttributeGroup(entityType, groupName);
        if (groupResp.ok) {
          const createdGroup = await groupResp.json();
          await createAttribute(createdGroup.id);
        } else {
          console.error(`could not create new group ${groupName}`);
        }
      } else {
        await createAttribute();
      }
    } catch (_error) {
      console.error(`could not create new custom attribute ${name}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing="sm">
      <Text color="dimmed">
        Vergib dem Feld einen Namen wie bspw. <i>Anzahl der Gäste</i> und wähle einen Typ aus. Optional kannst du eine
        detailliertere Beschreibung angeben und das Feld einer Gruppe zuordnen wie z.B. <i>Eckdaten</i>.
      </Text>

      <TextInput
        required
        label={i18n._('custom-attribute.name')}
        value={name}
        onChange={(event) => setName(replaceUmlaute(event.currentTarget.value))}
      />
      <Text size="xs" color="dimmed" mt={-8}>
        Kann später nicht geändert werden.
      </Text>

      <Textarea
        label={i18n._('custom-attribute.description')}
        value={description}
        onChange={(event) => setDescription(event.currentTarget.value)}
        autosize
        minRows={2}
      />

      {entityType !== 'contact' && (
        <Autocomplete
          label="Gruppe (optional)"
          description="Wähle eine bestehende Gruppe oder gib den Namen einer neuen Gruppe ein."
          data={groups.map((group) => group.name)}
          value={groupName}
          onChange={(value) => setGroupName(replaceUmlaute(value))}
        />
      )}
      {entityType !== 'contact' && (
        <Text size="xs" color="dimmed" mt={-8}>
          Kann später nicht geändert werden.
        </Text>
      )}

      <Select
        required
        label="Typ des Feldes"
        placeholder="Typ auswählen"
        data={DataS.getAttributeTypeOptions(i18n, true)}
        value={attributeType}
        onChange={setAttributeType}
      />

      <Group position="right" spacing="xs" mt="sm">
        <Button variant="default" onClick={() => closeModal(MODAL_ID)} disabled={loading}>
          {i18n._('actions.cancel')}
        </Button>
        <Button onClick={create} loading={loading}>
          {i18n._('actions.save')}
        </Button>
      </Group>
    </Stack>
  );
};
